/**
 * Pinia Store: Marketing Campaign
 *
 * Migrated from Vuex: src/extensions/marketing/store/modules/campaign.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import CampaignApi from '@/extensions/marketing/store/api/campaign';
import { useSetupStore } from '@/stores';

const form = {
    id: null,
    name: '',
    key: '',
    color: '#6b52ef',
    description: '',
};

const rules = {
    name: 'required',
    key: 'required|unique_marketing_campaign_key'
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false,
    }
};

export const useMarketingCampaignStore = defineStore('marketingCampaign', {
    state: () => ({
        loading: true,
        campaignsPaginated: {
            path: null,
            variables: {
                order: 'created_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {},
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions
        addPaginatedCampaignsToState(data) {
            this.campaignsPaginated.items = data.items;
            this.campaignsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.campaignsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.campaignsPaginated.link = _.cloneDeep(data.config.link);
            this.campaignsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.campaignsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.campaignsPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.campaignsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.campaignsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.campaignsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.campaignsPaginated.filters, key);
            } else {
                Vue.set(this.campaignsPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            this.campaignsPaginated.path = value;
        },

        defineSorting({ key, value }) {
            this.campaignsPaginated.variables.order = key;
            this.campaignsPaginated.variables.direction = value;
        },

        defineFilterVisibility(value) {
            this.campaignsPaginated.showFilters = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(newRules) {
            this.rules = newRules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);

            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            const campaign = this.form;

            if (campaign.id) {
                this.rules.key = `required|unique_marketing_campaign_key:"cms":${cms.id},"campaign":${campaign.id}`;
            } else {
                this.rules.key = `required|unique_marketing_campaign_key:"cms":${cms.id}`;
            }
        },

        resetUI() {
            this.ui.form.initialized = false;
            this.ui.form.validateOnEnter = false;
        },

        insertCampaign(campaign) {
            this.form = campaign;
        },

        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        // Async actions
        fetchCampaignsPaginated() {
            return CampaignApi.fetchCampaignsPaginated(this.campaignsPaginated).then(data => {
                this.addPaginatedCampaignsToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchCampaign(id) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (cms) {
                return CampaignApi.fetchCampaign(id, cms.id).then(campaign => {
                    this.insertCampaign(_.cloneDeep(campaign));
                    this.resetRules();
                });
            }
        },

        initializeCampaign(key) {
            this.resetRules();
        },

        create(campaign) {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (cms) {
                campaign.cms = { id: cms.id };
                return CampaignApi.create(campaign).then(campaign => {
                    this.insertCampaign(_.cloneDeep(campaign));
                    this.resetRules();
                });
            }
        },

        update(campaign) {
            return CampaignApi.update(campaign).then(campaign => {
                this.insertCampaign(_.cloneDeep(campaign));
                this.resetRules();
            });
        },

        deleteCampaign(id) {
            return CampaignApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertCampaign(_.cloneDeep(form));
        }
    }
});
